import { useState, useEffect } from "react";

const NODE_KINDS = [
  { value: "full", label: "Full", hint: "Camera + presence sensor + lux" },
  { value: "full_nolux", label: "Full (no lux)", hint: "Camera + presence sensor" },
  { value: "simple", label: "Simple", hint: "Presence sensor + lux" },
  { value: "simple_nolux", label: "Simple (no lux)", hint: "Presence sensor only" },
];

function kindFlags(kind) {
  return {
    hasCamera: kind === "full" || kind === "full_nolux",
    hasLux: kind === "full" || kind === "simple",
  };
}

function NodeEditor({ node, roomName, registryNodes = [], usedMacs = [], onRefreshNodes, onUpdate, onDelete, onDeselect, onSuggest }) {
  const [label, setLabel] = useState(node.label || "");

  useEffect(() => {
    setLabel(node.label || "");
  }, [node.id, node.label]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const setKind = (kind) => {
    const flags = kindFlags(kind);
    const patch = { kind, ...flags };
    if (flags.hasCamera && !node.camera) {
      patch.camera = { rotation: node.sensors?.[0]?.rotation ?? 0, fovAngle: 67, rangeM: 5 };
    }
    if (!flags.hasCamera) patch.camera = null;
    onUpdate(patch);
  };

  const updateSensor = (sid, patch) => {
    const sensors = (node.sensors || []).map((s) => (s.id === sid ? { ...s, ...patch } : s));
    onUpdate({ sensors });
  };

  const rotateSensor = (sid, delta) => {
    const s = (node.sensors || []).find((x) => x.id === sid);
    if (!s) return;
    const rot = ((((s.rotation || 0) + delta) % 360) + 360) % 360;
    updateSensor(sid, { rotation: rot });
  };

  const addSensor = () => {
    const sensors = node.sensors || [];
    const id = `s-${Date.now().toString(36)}`;
    onUpdate({
      sensors: [...sensors, { id, rotation: 0, fovAngle: 80, rangeM: 4, reliableM: 2 }],
    });
  };

  const removeSensor = (sid) => {
    const sensors = (node.sensors || []).filter((s) => s.id !== sid);
    if (sensors.length === 0) return; // keep at least one
    onUpdate({ sensors });
  };

  const updateCamera = (patch) => {
    onUpdate({ camera: { ...(node.camera || { rotation: 0, fovAngle: 67, rangeM: 5 }), ...patch } });
  };

  const rotateCamera = (delta) => {
    const cur = node.camera?.rotation || 0;
    updateCamera({ rotation: (((cur + delta) % 360) + 360) % 360 });
  };

  const numField = (val, onCommit, props = {}) => (
    <input
      type="number"
      defaultValue={val}
      onBlur={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onCommit(v);
      }}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );

  const linkedInfo = node.mac ? registryNodes.find((n) => n.mac === node.mac) : null;
  const availableNodes = registryNodes.filter((n) => !usedMacs.includes(n.mac));

  return (
    <div className="room-editor">
      <div className="room-editor-header">
        <h3>Node Editor</h3>
        <button className="btn-icon" onClick={onDeselect} title="Deselect">
          ✕
        </button>
      </div>

      <div className="room-editor-field">
        <label>Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => onUpdate({ label: label.trim() || "Node" })}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="room-editor-field">
        <label>Type</label>
        <div className="door-type-group">
          {NODE_KINDS.map((k) => (
            <button
              key={k.value}
              className={`door-type-btn ${node.kind === k.value ? "active" : ""}`}
              onClick={() => setKind(k.value)}
              title={k.hint}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      <div className="room-editor-field">
        <label>Room</label>
        <div className="readonly-field">{roomName || "— (outside rooms)"}</div>
      </div>

      <div className="node-section">
        <label className="toolbar-toggle path-toggle" title="Boundary of tracked coverage; not automatic crossing evidence">
          <input
            type="checkbox"
            checked={node.trackingEdge === true}
            onChange={(event) => onUpdate({ trackingEdge: event.target.checked })}
          />
          Tracking edge
        </label>
        <label className="toolbar-toggle path-toggle" title="A physical boundary of the home, distinct from the tracked coverage perimeter">
          <input
            type="checkbox"
            checked={node.trackingEdge === true && node.houseBoundary === true}
            disabled={node.trackingEdge !== true}
            onChange={(event) => onUpdate({ houseBoundary: event.target.checked })}
          />
          House boundary
        </label>
      </div>

      <div className="node-section">
        <div className="node-section-head">
          <span>Hardware</span>
          {onRefreshNodes && (
            <button className="btn-mini" onClick={onRefreshNodes} title="Refresh detected nodes">
              ↻
            </button>
          )}
        </div>
        {node.mac ? (
          <div className="hw-linked">
            <span className={`hw-dot ${linkedInfo?.online ? "on" : "off"}`} />
            <code className="hw-mac">{node.mac}</code>
            {linkedInfo?.alias && <span className="hw-alias">{linkedInfo.alias}</span>}
            {!linkedInfo && <span className="hw-missing" title="Not seen by the presence engine">unseen</span>}
            <button className="btn-mini btn-danger" onClick={() => onUpdate({ mac: null })}>
              Unlink
            </button>
          </div>
        ) : (
          <>
            <select
              className="hw-select"
              value=""
              onChange={(e) => e.target.value && onUpdate({ mac: e.target.value })}
            >
              <option value="">— Link detected node… —</option>
              {availableNodes.map((n) => (
                <option key={n.mac} value={n.mac}>
                  {(n.online ? "● " : "○ ") + (n.alias || n.mac)} ({n.mac})
                </option>
              ))}
            </select>
            {availableNodes.length === 0 && (
              <p className="editor-hint">No unlinked nodes detected yet.</p>
            )}
          </>
        )}
      </div>

      <div className="node-section">
        <div className="node-section-head">
          <span>Sensors ({(node.sensors || []).length})</span>
          <button className="btn-mini" onClick={addSensor}>
            + Sensor
          </button>
        </div>
        {(node.sensors || []).map((s, i) => (
          <div key={s.id} className="sensor-card">
            <div className="sensor-card-head">
              <span>Sensor {i + 1}</span>
              {(node.sensors || []).length > 1 && (
                <button className="btn-mini btn-danger" onClick={() => removeSensor(s.id)}>
                  Remove
                </button>
              )}
            </div>
            <div className="door-rotate-row">
              <button className="btn-secondary" onClick={() => rotateSensor(s.id, -15)}>
                ↺ 15°
              </button>
              <button className="btn-secondary" onClick={() => rotateSensor(s.id, 15)}>
                ↻ 15°
              </button>
            </div>
            <div className="room-editor-dimensions">
              <div className="dim-field">
                <label>Aim (°)</label>
                {numField(s.rotation, (v) => updateSensor(s.id, { rotation: ((v % 360) + 360) % 360 }), {
                  step: 5,
                })}
              </div>
              <div className="dim-field">
                <label>FOV (°)</label>
                {numField(s.fovAngle, (v) => updateSensor(s.id, { fovAngle: v }), { step: 5, min: 5, max: 180 })}
              </div>
            </div>
            <div className="room-editor-dimensions">
              <div className="dim-field">
                <label>Range (m)</label>
                {numField(s.rangeM, (v) => updateSensor(s.id, { rangeM: v }), { step: 0.5, min: 0.5 })}
              </div>
              <div className="dim-field">
                <label>Reliable (m)</label>
                {numField(s.reliableM, (v) => updateSensor(s.id, { reliableM: v }), { step: 0.5, min: 0.5 })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {node.hasCamera && node.camera && (
        <div className="node-section">
          <div className="node-section-head">
            <span>Camera</span>
          </div>
          <div className="door-rotate-row">
            <button className="btn-secondary" onClick={() => rotateCamera(-15)}>
              ↺ 15°
            </button>
            <button className="btn-secondary" onClick={() => rotateCamera(15)}>
              ↻ 15°
            </button>
          </div>
          <div className="room-editor-dimensions">
            <div className="dim-field">
              <label>Aim (°)</label>
              {numField(node.camera.rotation, (v) => updateCamera({ rotation: ((v % 360) + 360) % 360 }), {
                step: 5,
              })}
            </div>
            <div className="dim-field">
              <label>FOV (°)</label>
              {numField(node.camera.fovAngle, (v) => updateCamera({ fovAngle: v }), { step: 5, min: 5, max: 180 })}
            </div>
          </div>
          <div className="room-editor-dimensions">
            <div className="dim-field">
              <label>Range (m)</label>
              {numField(node.camera.rangeM, (v) => updateCamera({ rangeM: v }), { step: 0.5, min: 0.5 })}
            </div>
          </div>
        </div>
      )}

      {onSuggest && (
        <button className="btn-secondary suggest-btn" onClick={onSuggest}>
          ✨ Suggest placement
        </button>
      )}

      <p className="editor-hint">Drag the node on the map to position it on a wall.</p>

      <div className="room-editor-actions">
        <button className="btn-secondary btn-danger" onClick={onDelete}>
          Delete Node
        </button>
      </div>
    </div>
  );
}

export default NodeEditor;
